// src/console/pages/system/securityGates.ts — the rules the "Security gates"
// group on Launch controls reads by (ENG-1385). Pure: no React, no requests.
//
// The api decides everything here (ENG-1331, D-9). This module only describes
// it: which controls are gates, who a gate covers, and whether a change widens
// (an arm) or narrows (a disarm) that cover, so the console can word a change
// before it is sent. When the api answers differently from the description, the
// card shows the api's answer.
import {
  apiCoverage,
  cohortMembers,
  isMisconfigured,
  type CohortChange,
  type LaunchControl,
  type LaunchControlAuditRow,
  type LaunchControlCohortUser,
  type LaunchControlRollout,
  type SecurityGateChangeKind,
  type SecurityGateDisarmRule,
  type SecurityGatePerson,
} from "../../../services/launchControlService";

/**
 * A control is a security gate only when the api marks it so. Never by key
 * prefix: `auth.balance_pay_stepup_enforced` keeps domain auth, and a later
 * gate appears with no console change.
 */
export const isSecurityGate = (control: LaunchControl): boolean =>
  control.securityGate === true;

/** Who a gate protects, for the card. */
export type GateCoverage =
  | { kind: "off" }
  | { kind: "everyone" }
  | { kind: "cohort"; size: number; members: LaunchControlCohortUser[] }
  | { kind: "nobody" };

/** Who a stored row covers, as the api's gate reader reads it. Carries ids. */
export type GateReach =
  | { kind: "nobody" }
  | { kind: "everyone" }
  | { kind: "cohort"; userIds: string[] };

interface GateRow {
  enabled: boolean;
  rollout?: Pick<
    LaunchControlRollout,
    "userIds" | "businessIds" | "percentage"
  > | null;
  /** The api's flag: ON with no explicit percentage, read by the decided rule. */
  misconfigured?: boolean;
}

const normaliseIds = (ids: readonly string[] | undefined): string[] =>
  [...new Set((ids ?? []).map((id) => String(id).trim().toLowerCase()))]
    .filter(Boolean)
    .sort();

/**
 * ENG-1331's reader rules: OFF → nobody. ON with no targeting, or a
 * percentage of 100 or more → everyone. ON with named users → exactly those
 * people, with or without a percentage (never everyone, which is what the
 * generic reader would say). ON with a rollout that names nobody → nobody.
 * ON with nobody named and no percentage is everyone on the older api, and
 * nobody on a row the api flags misconfigured, whose reader follows the
 * decided rule.
 */
export const reachOf = (row: GateRow): GateReach => {
  if (!row.enabled) return { kind: "nobody" };
  const noTargeting: GateReach = isMisconfigured(row)
    ? { kind: "nobody" }
    : { kind: "everyone" };
  const rollout = row.rollout;
  if (!rollout) return noTargeting;
  const percentage =
    typeof rollout.percentage === "number" ? rollout.percentage : undefined;
  if (percentage !== undefined && percentage >= 100)
    return { kind: "everyone" };
  const userIds = normaliseIds(rollout.userIds);
  if (userIds.length) return { kind: "cohort", userIds };
  if (percentage !== undefined || (rollout.businessIds ?? []).length)
    return { kind: "nobody" };
  return noTargeting;
};

/**
 * Who a gate protects. The api's `coverage` wins whenever it is sent; without
 * it the same rules are applied to the stored rollout.
 */
export const coverageOf = (control: LaunchControl): GateCoverage => {
  if (!control.enabled) return { kind: "off" };
  const members = cohortMembers(control.rollout);
  const coverage = apiCoverage(control);
  const kind = coverage?.kind ?? reachOf(control).kind;
  if (kind === "cohort") {
    const size =
      typeof coverage?.people === "number" ? coverage.people : members.length;
    return { kind: "cohort", size, members };
  }
  return kind === "everyone" ? { kind: "everyone" } : { kind: "nobody" };
};

/**
 * A stored row carrying a setting no gate reads: businesses, or a percentage
 * between 1 and 99. ENG-1331's boot report flags the same rows.
 */
export const carriesIgnoredSetting = (control: LaunchControl): boolean => {
  const rollout = control.rollout;
  if (!rollout) return false;
  const percentage = rollout.percentage;
  return (
    (rollout.businessIds ?? []).length > 0 ||
    (typeof percentage === "number" && percentage > 0 && percentage < 100)
  );
};

/** Is everyone `inner` covers also covered by `outer`? */
const covers = (outer: GateReach, inner: GateReach): boolean => {
  if (inner.kind === "nobody" || outer.kind === "everyone") return true;
  if (outer.kind === "nobody" || inner.kind === "everyone") return false;
  const outerIds = new Set(outer.userIds);
  return inner.userIds.every((id) => outerIds.has(id));
};

/**
 * ENG-1331 step 5: anyone covered before and not after is a disarm (OFF,
 * removing people, everyone → cohort, a mixed add-and-remove). A wider cover
 * that keeps everyone is an arm. No change in who is covered is neutral (a
 * cohort edit while the gate is OFF). For wording only: the api decides.
 */
export const classifyChange = (
  before: GateReach,
  after: GateReach
): SecurityGateChangeKind => {
  if (!covers(after, before)) return "disarm";
  return covers(before, after) ? "neutral" : "arm";
};

/**
 * Who the gate would cover after turning it on or off, modelled on what the
 * api's write stores rather than on re-reading the stored row: ON with nobody
 * named and no stored percentage stores 100 (everyone), ON with people named
 * and no stored percentage stores 0 (those people), and a stored percentage is
 * kept. The older api reads those rows the same way, so the preview does not
 * depend on the misconfigured flag.
 */
export const reachAfterToggle = (
  control: LaunchControl,
  enabled: boolean
): GateReach => {
  if (!enabled) return { kind: "nobody" };
  const userIds = control.rollout?.userIds ?? [];
  const businessIds = control.rollout?.businessIds ?? [];
  const stored = control.rollout?.percentage;
  const named = normaliseIds(userIds).length > 0 || businessIds.length > 0;
  return reachOf({
    enabled: true,
    rollout: {
      userIds,
      businessIds,
      percentage: typeof stored === "number" ? stored : named ? 0 : 100,
    },
  });
};

/**
 * Who the gate would cover after a cohort change, computed as the api does:
 * the stored ids plus the added minus the removed, and the stored percentage
 * (0 when none) unless the change sets one.
 */
export const reachAfterCohort = (
  control: LaunchControl,
  change: CohortChange
): GateReach => {
  const removed = new Set(normaliseIds(change.removeUserIds));
  const userIds = normaliseIds([
    ...(control.rollout?.userIds ?? []),
    ...(change.addUserIds ?? []),
  ]).filter((id) => !removed.has(id));
  const stored = control.rollout?.percentage;
  return reachOf({
    enabled: control.enabled,
    rollout: {
      userIds,
      businessIds: control.rollout?.businessIds ?? [],
      percentage:
        change.percentage ?? (typeof stored === "number" ? stored : 0),
    },
  });
};

/** The change's effect in people, for the cohort editor's preview line. */
export const peopleDelta = (
  before: GateReach,
  after: GateReach
): { added: string[]; removed: string[] } => {
  const ids = (reach: GateReach) =>
    reach.kind === "cohort" ? reach.userIds : [];
  const was = new Set(ids(before));
  const now = new Set(ids(after));
  return {
    added: ids(after).filter((id) => !was.has(id)),
    removed: ids(before).filter((id) => !now.has(id)),
  };
};

const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

/** Who a gate protects, in words. Never a user id. */
export const coverageWords = (coverage: GateCoverage): string => {
  switch (coverage.kind) {
    case "off":
      return "nobody (OFF)";
    case "everyone":
      return "everyone";
    case "cohort":
      return people(coverage.size);
    default:
      return "nobody";
  }
};

const ROLE_WORDS: Record<string, string> = {
  "admin.founder": "the founder",
  "admin.super": "a super-admin",
  "admin.finance": "the head of finance",
  "admin.compliance": "compliance",
  "admin.support_lead": "a support lead",
};

/** "the founder or a super-admin", from the approver role keys the api names. */
export const approversInWords = (roleKeys?: readonly string[]): string => {
  const words = [
    ...new Set((roleKeys ?? []).map((key) => ROLE_WORDS[key] ?? key)),
  ];
  if (!words.length) return "";
  if (words.length === 1) return words[0];
  return `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;
};

/**
 * The group's last introductory sentence. From the api's disarm rule when it
 * sends one; otherwise a sentence that claims no specific rule.
 */
export const disarmRuleSentence = (rule?: SecurityGateDisarmRule | null) => {
  if (!rule)
    return "Turning one off, or covering fewer people, may need a second approver; the server decides.";
  if (!rule.twoPeople)
    return "Turning one off, or covering fewer people, is one person's decision.";
  return "Turning one off, or covering fewer people, needs a second approver.";
};

/**
 * What a disarm needs, for its confirm and the cohort editor's preview. From
 * the api's rule only; without it, a sentence that claims no specific rule.
 */
export const disarmNeeds = (rule?: SecurityGateDisarmRule | null): string => {
  if (!rule) return "A second person may need to approve this.";
  const parts: string[] = [];
  if (rule.twoPeople) {
    const who = approversInWords(rule.approverRoleKeys);
    parts.push(
      who
        ? `A second person (${who}) must approve this`
        : "A second person must approve this"
    );
  }
  if (rule.operatorTotp)
    parts.push(
      parts.length
        ? "you'll need your authenticator code"
        : "You'll need your authenticator code"
    );
  let sentence = parts.length ? `${parts.join(", and ")}.` : "";
  if (rule.rollbackWindowMinutes > 0)
    sentence += `${sentence ? " " : ""}Within ${rule.rollbackWindowMinutes} minutes of the last arm, whoever armed it may undo that arm alone.`;
  return sentence || "This applies at once.";
};

/** A display name, or null when what the api sent is really an id. */
export const displayName = (
  person?: SecurityGatePerson | null
): string | null => {
  const name = person?.name?.trim();
  if (!name || name === person?.id || /^[a-f\d]{24}$/i.test(name)) return null;
  return name;
};

/** The words for the last change, from the change kind and where it left the gate. */
export const lastChangeWhat = (
  change: SecurityGateChangeKind,
  now: GateCoverage,
  source?: "console" | "seed" | "detected"
): string => {
  // The seed writes a row with no before-state, which the api reads as neutral.
  if (source === "seed")
    return now.kind === "off"
      ? "seeded OFF"
      : `seeded ON for ${coverageWords(now)}`;
  if (change === "neutral")
    return now.kind === "off"
      ? "cohort edited while OFF"
      : "edited, with no change in who is protected";
  if (change === "arm")
    return now.kind === "everyone"
      ? "turned on for everyone"
      : now.kind === "cohort"
        ? `armed for ${people(now.size)}`
        : "armed";
  if (now.kind === "off") return "turned off";
  if (now.kind === "cohort") return `narrowed to ${people(now.size)}`;
  return "narrowed to nobody";
};

// ── History, from the audit trail ──────────────────────────────────────────

/** Coverage as the api's audit row records it ("everyone", "cohort:<ids>"). */
const reachOfFingerprint = (value: unknown): GateReach | undefined => {
  if (value === "everyone") return { kind: "everyone" };
  if (value === "nobody") return { kind: "nobody" };
  if (typeof value !== "string" || !value.startsWith("cohort:"))
    return undefined;
  return {
    kind: "cohort",
    userIds: normaliseIds(value.slice("cohort:".length).split(",")),
  };
};

export const reachWords = (reach: GateReach): string =>
  reach.kind === "cohort" ? people(reach.userIds.length) : reach.kind;

export interface GateHistoryEntry {
  id: string;
  at?: string;
  who: string;
  before: string;
  after: string;
  /** Arm or disarm (neutral reads as an edit), when the row says. */
  change?: SecurityGateChangeKind;
  /** "added 2 people", "turned off" … */
  what: string;
}

/** A populated audit actor's name, else "a team member". Never an id. */
export const auditActorName = (row: LaunchControlAuditRow): string => {
  const actor = typeof row.userId === "object" ? row.userId : undefined;
  const pi = actor?.personal_info;
  const name =
    pi?.fullName?.trim() ||
    [pi?.firstName, pi?.lastName].filter(Boolean).join(" ").trim() ||
    pi?.name?.trim();
  if (name) return name;
  // The seed and the change detector write as a system role, not a person.
  if (/^(migration|system|seed)/i.test(row.actorRole ?? ""))
    return "the seed script";
  if (row.action === "launch_control.seeded") return "the seed script";
  return "a team member";
};

const describeStep = (
  before: GateReach | undefined,
  after: GateReach | undefined,
  enabledBefore: unknown,
  enabledAfter: unknown
): string => {
  if (enabledBefore === true && enabledAfter === false) return "turned off";
  if (enabledBefore === false && enabledAfter === true)
    return after?.kind === "everyone"
      ? "turned on for everyone"
      : after?.kind === "cohort"
        ? `turned on for ${people(after.userIds.length)}`
        : "turned on";
  if (before && after) {
    if (after.kind === "everyone" && before.kind !== "everyone")
      return "turned on for everyone";
    if (after.kind === "nobody" && before.kind !== "nobody")
      return "narrowed to nobody";
    const { added, removed } = peopleDelta(before, after);
    const parts = [
      added.length ? `added ${people(added.length)}` : "",
      removed.length ? `removed ${people(removed.length)}` : "",
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
    if (before.kind === "everyone" && after.kind === "cohort")
      return `narrowed to ${people(after.userIds.length)}`;
  }
  if (enabledBefore === false && enabledAfter === true) return "turned on";
  return "cohort edited";
};

/**
 * One gate's recent history from launch-control audit rows: only rows whose
 * `targetKey` is the gate's key, newest first, at most `limit`.
 */
export const gateHistory = (
  rows: readonly LaunchControlAuditRow[] | undefined,
  key: string,
  limit = 20
): GateHistoryEntry[] =>
  (rows ?? [])
    .filter((row) => row.targetKey === key && row.success !== false)
    .sort(
      (a, b) =>
        Date.parse(b.createdAt ?? "") - Date.parse(a.createdAt ?? "") || 0
    )
    .slice(0, limit)
    .map((row, index) => {
      const before = reachOfFingerprint(row.before?.coverage);
      const after = reachOfFingerprint(row.after?.coverage);
      const recorded = row.after?.change;
      const change =
        recorded === "arm" || recorded === "disarm" || recorded === "neutral"
          ? recorded
          : before && after
            ? classifyChange(before, after)
            : undefined;
      const onOff = (value: unknown) =>
        value === true ? "on" : value === false ? "off" : "—";
      return {
        id: row._id ?? `${row.createdAt ?? ""}-${index}`,
        at: row.createdAt,
        who:
          row.action === "security_gate.unaudited_change_detected"
            ? "no audited actor"
            : auditActorName(row),
        before: before ? reachWords(before) : onOff(row.before?.enabled),
        after: after ? reachWords(after) : onOff(row.after?.enabled),
        change,
        what:
          row.action === "launch_control.seeded"
            ? "seeded"
            : row.action === "security_gate.unaudited_change_detected"
              ? "changed outside the console (detected)"
              : describeStep(
                  before,
                  after,
                  row.before?.enabled,
                  row.after?.enabled
                ),
      };
    });
